/**
 * Jovii — YOUTUBE AS A DECODER, NOT AS AN INTERFACE.
 *
 * There is no non-YouTube trailer. Measured over 233 Cinemeta titles and 80
 * TMDB title pages, the number of titles with a trailer we could stream into
 * our own player is ZERO (docs/TRAILERS.md § A6). Cinemeta cannot even express
 * one — the only keys on 390 trailer entries are `source`, `title`, `type` and
 * `ytId`. So "prefer a non-YouTube source" cannot mean a different supplier. It
 * can only mean a different SURFACE: take YouTube's interface away and use its
 * player as a video decoder behind our own picture and our own controls.
 *
 * This module is what makes that possible. It is a thin wrapper over YouTube's
 * IFrame API, which is loaded by `<script src>` (so it needs no CORS) and
 * attached to an iframe WE built — `new YT.Player(existingElement)` does not
 * take the markup away from us, so the `allow` list, the crop and the
 * `pointer-events: none` stay ours.
 *
 * What it buys, all verified in Chromium AND WebKit (docs/TRAILERS.md § B2, B7):
 *
 *   • `PLAYING` — the only honest "there is a picture now" signal. The iframe's
 *     own `load` event fires 444 ms EARLIER and fires identically for a video
 *     that will never play, which is why the fade used to be on a blind timer.
 *   • `onError` — 150/101 mean the uploader has disabled embedding; ~1 title in
 *     10. It arrives in about half a second, so a dead trailer is known before
 *     anybody has waited.
 *   • `unloadModule('captions')` — `cc_load_policy=0` is NOT enough. Measured
 *     A/B at the same frame: with the parameter alone "[Music]" is burned
 *     across the picture; with the call it is clean.
 *   • duration / currentTime / seekTo / play / pause / mute — everything the
 *     title-page trailer needs to wear our chrome instead of YouTube's.
 *
 * WHAT IT CANNOT DO: reach the volume through `player/loudness.ts` (the frame
 * is cross-origin), unmute without a real gesture, or tell us the video's
 * dimensions. See docs/TRAILERS.md § C5.
 */

/* ────────────────────────────────────────────────────────────────────────────
 * The embed URL, written once for every surface
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * THE ONE EMBED URL. `Trailer.svelte` (banner, title sheet) and the tile stage
 * (`tile-stage.svelte.ts`) must ship byte-identical parameters, or the two
 * surfaces drift on the exact behaviours docs/TRAILERS.md § B3 measured one by
 * one. Every parameter here is load-bearing or verifiably harmless:
 *
 *   • `playsinline=1` — without it iOS takes the video full screen over the app.
 *   • `mute` — muted is the only autoplay a phone permits.
 *   • `controls=0` — removes the transport bar (NOT the title bar or wordmark;
 *     the branding wait in trailer-rules.ts covers those).
 *   • `cc_load_policy=0` — not sufficient alone; `attachYt` unloads the
 *     captions module too, or "[Music]" is burned across the picture.
 *   • `enablejsapi=1` — THE one that matters: without it there is no PLAYING,
 *     no onError, and no way to know whether this device plays anything at all.
 *   • `modestbranding` is deliberately absent: deprecated and measured inert.
 */
export function embedUrl(id: string, opts: { muted?: boolean; start?: number } = {}): string {
  const p = new URLSearchParams({
    autoplay: '1',
    mute: opts.muted === false ? '0' : '1',
    playsinline: '1',
    controls: '0',
    rel: '0',
    iv_load_policy: '3',
    fs: '0',
    disablekb: '1',
    // Not a loop: a trailer that has finished should stop.
    loop: '0',
    cc_load_policy: '0',
    enablejsapi: '1'
  });
  const from = opts.start ?? 0;
  if (from > 0) p.set('start', String(Math.floor(from)));
  const origin = typeof location === 'undefined' ? '' : location.origin;
  if (origin) p.set('origin', origin);
  return `https://www.youtube-nocookie.com/embed/${id}?${p.toString()}`;
}

/* ────────────────────────────────────────────────────────────────────────────
 * The API script, loaded once for the whole page
 * ──────────────────────────────────────────────────────────────────────────── */

const API_SRC = 'https://www.youtube.com/iframe_api';

interface YtPlayerApi {
  playVideo(): void;
  pauseVideo(): void;
  mute(): void;
  unMute(): void;
  isMuted(): boolean;
  setVolume(v: number): void;
  getVolume(): number;
  seekTo(seconds: number, allowSeekAhead: boolean): void;
  setPlaybackRate(rate: number): void;
  stopVideo(): void;
  getCurrentTime(): number;
  getDuration(): number;
  getVideoLoadedFraction(): number;
  getPlayerState(): number;
  loadVideoById(options: { videoId: string; startSeconds?: number }): void;
  unloadModule(name: string): void;
  destroy(): void;
}

interface YtApi {
  Player: new (
    element: HTMLElement | string,
    options: {
      events: {
        onReady?: (event: { target: YtPlayerApi }) => void;
        onStateChange?: (event: { data: number; target: YtPlayerApi }) => void;
        onError?: (event: { data: number; target: YtPlayerApi }) => void;
      };
    }
  ) => YtPlayerApi;
  PlayerState: { UNSTARTED: -1; ENDED: 0; PLAYING: 1; PAUSED: 2; BUFFERING: 3; CUED: 5 };
}

type Windowish = Window & {
  YT?: YtApi;
  onYouTubeIframeAPIReady?: () => void;
};

let apiPromise: Promise<YtApi> | null = null;

/**
 * The API script, exactly once per page.
 *
 * `onYouTubeIframeAPIReady` is a single global the script calls when it is
 * ready, so it is set here before the tag is inserted and the promise is what
 * everybody else waits on. A second caller gets the same promise; a caller that
 * arrives after it has resolved gets `window.YT` immediately.
 */
export function loadYtApi(): Promise<YtApi> {
  if (apiPromise !== null) return apiPromise;
  const w = globalThis as unknown as Windowish;
  if (typeof document === 'undefined') {
    apiPromise = Promise.reject(new Error('no document'));
    return apiPromise;
  }
  if (w.YT?.Player) {
    apiPromise = Promise.resolve(w.YT);
    return apiPromise;
  }
  apiPromise = new Promise<YtApi>((resolve, reject) => {
    const previous = w.onYouTubeIframeAPIReady;
    w.onYouTubeIframeAPIReady = (): void => {
      previous?.();
      if (w.YT?.Player) resolve(w.YT);
      else reject(new Error('iframe_api loaded without YT.Player'));
    };
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${API_SRC}"]`);
    if (existing !== null) return;
    const tag = document.createElement('script');
    tag.src = API_SRC;
    tag.async = true;
    // A blocked script (an extension, an offline phone) must not leave every
    // trailer on the page waiting for ever: the still is the product, and a
    // rejected promise is what tells the stage to keep it.
    tag.onerror = (): void => reject(new Error('iframe_api blocked'));
    document.head.appendChild(tag);
  });
  return apiPromise;
}

/* ────────────────────────────────────────────────────────────────────────────
 * One attached player
 * ──────────────────────────────────────────────────────────────────────────── */

export const PLAYING = 1;
export const PAUSED = 2;
export const ENDED = 0;

export interface YtHandlers {
  /** The player is attached. Captions are already unloaded by the time this runs. */
  onready?: () => void;
  /** There is decoded video on screen. THE signal — see the header. */
  onplaying?: () => void;
  /** 2 bad parameter, 5 player failure, 100 gone, 101/150 embedding disabled. */
  onerror?: (code: number) => void;
  /** Any state change, for a surface that draws its own play/pause. */
  onstate?: (state: number) => void;
}

export interface YtHandle {
  play(): void;
  pause(): void;
  /**
   * Switch this player to another video and autoplay it — the persistent tile
   * stage's whole trick. Measured (docs/TRAILERS.md § B8, re-measured
   * 2026-08-19): a fresh iframe reaches PLAYING in ~800–2200 ms; `load` on a
   * warm player in 124–470 ms, with the 1.1 MB player shell paid once.
   */
  load(videoId: string, startSeconds?: number): void;
  /**
   * Stop and unload the current video, keeping the player. What the stage
   * calls when its owner releases: the pipeline goes quiet, no bytes flow,
   * and the NEXT `load` reuses everything.
   */
  stop(): void;
  mute(): void;
  /** GESTURE ONLY. Called from a timer it is refused, everywhere, always. */
  unmute(): void;
  muted(): boolean;
  seek(seconds: number): void;
  /**
   * DISCRETE RUNGS ONLY. YouTube accepts 0.25–2 in 0.25 steps and rounds
   * anything else toward 1, so a "gentle 1.02×" is not a thing this player
   * can do — a corrector nudges at 1.25×/0.75× or it does not nudge at all.
   */
  rate(r: number): void;
  time(): number;
  duration(): number;
  /** 0–1, for a buffer bar that is ours rather than YouTube's. */
  loaded(): number;
  state(): number;
  destroy(): void;
}

/** Neither call throws when the module is absent, and both are needed. */
function silenceCaptions(player: YtPlayerApi): void {
  try {
    player.unloadModule('captions');
    player.unloadModule('cc');
  } catch {
    /* an older player, or one that has not built its module list yet */
  }
}

/**
 * Attach to an iframe that already has `enablejsapi=1` in its URL.
 *
 * Resolves as soon as the player object EXISTS, not when it is ready — a frame
 * that never becomes ready is exactly the iPhone case, and a promise that never
 * settles would strand the teardown that has to destroy it. Readiness arrives
 * as `onready`; a frame that says nothing at all is somebody else's decision to
 * make, and `trailer-rules.ts`'s `SILENCE_MS` makes it.
 *
 * Rejects only if the API script itself cannot be loaded, whose answer is the
 * same as every other refusal here: keep the still.
 */
export async function attachYt(
  iframe: HTMLIFrameElement,
  handlers: YtHandlers = {}
): Promise<YtHandle> {
  const api = await loadYtApi();
  let player: YtPlayerApi | null = null;

  /**
   * EVERY CALL IS GUARDED, and not out of habit: the player is a cross-origin
   * postMessage bridge, so a method called after the frame has gone — a rail
   * scrolled away mid-teardown — throws from inside YouTube's own code and
   * would take the Svelte effect that called it down with it.
   */
  const safe = <T>(fn: (p: YtPlayerApi) => T, fallback: T): T => {
    if (player === null) return fallback;
    try {
      return fn(player);
    } catch {
      return fallback;
    }
  };

  player = new api.Player(iframe, {
    events: {
      onReady: (event) => {
        // The captions module re-loads itself when the video's own track list
        // arrives, so this is called here AND on PLAYING. `cc_load_policy=0`
        // alone leaves "[Music]" burned across the picture.
        silenceCaptions(event.target);
        handlers.onready?.();
      },
      onStateChange: (event) => {
        if (event.data === PLAYING) {
          silenceCaptions(event.target);
          handlers.onplaying?.();
        }
        handlers.onstate?.(event.data);
      },
      onError: (event) => handlers.onerror?.(event.data)
    }
  });

  return {
    play: () => safe((p) => p.playVideo(), undefined),
    pause: () => safe((p) => p.pauseVideo(), undefined),
    load: (videoId: string, startSeconds?: number) =>
      safe((p) => p.loadVideoById(startSeconds ? { videoId, startSeconds } : { videoId }), undefined),
    stop: () => safe((p) => p.stopVideo(), undefined),
    mute: () => safe((p) => p.mute(), undefined),
    unmute: () => safe((p) => p.unMute(), undefined),
    muted: () => safe((p) => p.isMuted(), true),
    seek: (seconds: number) => safe((p) => p.seekTo(Math.max(0, seconds), true), undefined),
    rate: (r: number) => safe((p) => p.setPlaybackRate(r), undefined),
    time: () => safe((p) => p.getCurrentTime(), 0),
    duration: () => safe((p) => p.getDuration(), 0),
    loaded: () => safe((p) => p.getVideoLoadedFraction(), 0),
    state: () => safe((p) => p.getPlayerState(), -1),
    destroy: () => {
      const p = player;
      player = null;
      try {
        p?.destroy();
      } catch {
        /* the frame is already gone, which is the outcome we wanted */
      }
    }
  };
}
